import React, { memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Box, Typography } from '@mui/material';
import { trackEvent } from '@surfline/web-common';

import { CameraInsightsIcon } from 'components/Icons/CameraInsightsIcon';
import CamListItemSpotInfo from 'components/CamListItemSpotInfo';
import InternalCamBadge from 'components/InternalCamBadge';
import CamListLink from 'components/CamListLink';
import CurrentWaveRating from 'components/CurrentWaveRating';
import ObservedBadge from 'components/ObservedBadge';
import { PremiumCamLabel } from 'components/CTAs';
import type { SpotReportData } from 'types/spot';
import type { SurfHeight as TSurfHeight } from 'types/units';
import conditionIsFlat from 'utils/conditionIsFlat';
import formatWaveHeight from 'utils/formatWaveHeight';

// @ts-expect-error Image can't be found on this path
import noCamBackground from '../../../public/images/no-cam-background.png';

import styles from './CamListItem.module.scss';

interface Props {
  spot: SpotReportData;
  units: TSurfHeight;
  showPremiumCTA?: boolean;
}

const getSpotCamera = (spot: SpotReportData) => spot?.cameras[0];

const getBackgroundImg = (spot: SpotReportData) =>
  getSpotCamera(spot) ? spot.cameras[0].stillUrl : noCamBackground;

const getClass = (hasCam: boolean, isCurrentSpot: boolean, insightsEnabled: boolean) =>
  classNames(
    'sl-cam-list-item',
    hasCam && styles.camListItem,
    !hasCam && styles.camListItemWithoutCam,
    isCurrentSpot && styles.camListItemCurrent,
    isCurrentSpot && 'sl-cam-list-item-current',
    insightsEnabled && 'sl-cam-list-item-insights-enabled',
    insightsEnabled && isCurrentSpot && styles.camListItemCurrentInsightsEnabled,
    insightsEnabled && isCurrentSpot && 'sl-cam-list-item-current-insights-enabled',
  );

const CamListItem: React.FC<Props> = ({ spot, units, showPremiumCTA = false }) => {
  const router = useRouter();
  const { spotId } = router.query;

  const isFlat = conditionIsFlat(spot.waveHeight.max, units);

  const showObservedBadge = useMemo(
    () => spot.waveHeight?.type === 'HUMAN' || spot.waveHeight?.type === 'CAMERA',
    [spot.waveHeight?.type],
  );

  const handleSegmentEvent = useCallback(
    () =>
      trackEvent('Clicked Nearby Spot', {
        spotId: spot._id,
        spotName: spot.name,
        clickedLocation: 'camera player',
      }),
    [spot._id, spot.name],
  );

  const spotHasCam = !!getSpotCamera(spot);
  const supportsInsights = spot?.cameras?.some(
    (camera) => camera.supportsInsights && spot.insightsCameraId,
  );

  const thumbnailBackground = useMemo(
    () => ({ src: getBackgroundImg(spot), alt: `${spot.name} Camera` }),
    [spot],
  );

  return (
    <CamListLink spot={spot} disabled={spot._id === spotId} segmentEvent={handleSegmentEvent}>
      <div className={getClass(spotHasCam, spotId === spot._id, supportsInsights)}>
        {!spotHasCam && (
          <div className={styles.thumbnailNoCam}>
            <Typography variant="callout1" component="p" className={styles.noCamLabel}>
              No cam
            </Typography>
          </div>
        )}
        {getSpotCamera(spot)?.isInternalCam && <InternalCamBadge />}
        {supportsInsights && (
          <div className={styles.cameraInsightsIconWrapper}>
            <CameraInsightsIcon />
          </div>
        )}
        {showPremiumCTA && (
          <div className={styles.topContainer}>
            <PremiumCamLabel />
          </div>
        )}
        <div
          className={classNames({ [styles.details]: true, [styles.detailsWithCam]: spotHasCam })}
          data-testid="cam-details"
        >
          <Box className={styles.detailsContainer}>
            {spot.conditions.value && (
              <Box className={styles.currentWaveRating}>
                <CurrentWaveRating rating={spot.conditions.value} direction="VERTICAL" />
              </Box>
            )}
            <Box className={styles.surfHeightContainer}>
              <Box className={styles.surfHeight}>
                {isFlat ? (
                  <Typography component="p" variant="title3" data-testid="cam-list-item-flat-text">
                    Flat
                  </Typography>
                ) : (
                  <Typography component="p" data-testid="cam-list-item-wave-height">
                    <Typography
                      component="span"
                      variant="title3"
                      data-testid="cam-list-item-wave-height-value"
                    >
                      {spot.waveHeight.min}-{spot.waveHeight.max}
                    </Typography>
                    <Typography
                      component="span"
                      variant="callout1"
                      data-testid="cam-list-item-wave-height-units"
                    >
                      {formatWaveHeight(units, true)}
                    </Typography>
                    {spot.waveHeight.plus && (
                      <Typography
                        component="span"
                        variant="title3"
                        data-testid="cam-list-item-wave-height-plus"
                      >
                        +
                      </Typography>
                    )}
                  </Typography>
                )}
              </Box>
              {showObservedBadge && (
                <Box className={styles.observedBadge} data-testid="cam-list-item-observed-badge">
                  <ObservedBadge />
                </Box>
              )}
            </Box>
          </Box>
        </div>
        <div className={styles.thumbnail}>
          <Image
            alt={thumbnailBackground.alt}
            data-testid="cam-thumbnail"
            layout="fill"
            loading="lazy"
            src={thumbnailBackground.src}
            unoptimized
          />
        </div>
      </div>
      <div className={styles.spotInfo}>
        <CamListItemSpotInfo spot={spot} />
      </div>
    </CamListLink>
  );
};

export default memo(CamListItem);
